export const PAGINATOR_STORAGE_PREFIX = 'paginator::';

export type StoredPaginatorState = {
  version: 2;
  savedAtIso: string;
  valuesByFieldKey: {
    pageNumber: number; // 1-based (backend)
    pageSize: number;
  };
};

export function getPaginatorStorageKey(tableName: string): string | null {
  const normalizedTableName = String(tableName ?? '').trim();

  if (normalizedTableName.length === 0) {
    return null;
  }

  return `${PAGINATOR_STORAGE_PREFIX}${normalizedTableName}`;
}

export function loadStoredPaginatorState(tableName: string): StoredPaginatorState | null {
  const storageKey = getPaginatorStorageKey(tableName);

  if (!storageKey) {
    return null;
  }

  const raw = localStorage.getItem(storageKey);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as StoredPaginatorState;

    if (!isValidStoredPaginatorState(parsed)) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function persistPaginatorState(params: {
  tableName: string;
  pageNumber: number;
  pageSize: number;
}): StoredPaginatorState | null {
  const storageKey = getPaginatorStorageKey(params.tableName);

  if (!storageKey) {
    return null;
  }

  const state: StoredPaginatorState = {
    version: 2,
    savedAtIso: new Date().toISOString(),
    valuesByFieldKey: {
      pageNumber: params.pageNumber,
      pageSize: params.pageSize
    }
  };

  localStorage.setItem(storageKey, JSON.stringify(state));

  return state;
}

export function resetPaginatorStateToFirstPage(params: {
  tableName: string;
  pageSize: number;
}): StoredPaginatorState | null {
  return persistPaginatorState({
    tableName: params.tableName,
    pageNumber: 1,
    pageSize: params.pageSize
  });
}

export function clearPaginatorState(tableName: string): void {
  const storageKey = getPaginatorStorageKey(tableName);

  if (!storageKey) {
    return;
  }

  localStorage.removeItem(storageKey);
}

function isValidStoredPaginatorState(value: any): value is StoredPaginatorState {
  if (!value) {
    return false;
  }

  if (value.version !== 2) {
    return false;
  }

  const valuesByFieldKey = value.valuesByFieldKey;

  if (!valuesByFieldKey) {
    return false;
  }

  const pageNumber = valuesByFieldKey.pageNumber;
  const pageSize = valuesByFieldKey.pageSize;

  const isValidPageNumber = Number.isFinite(pageNumber) && pageNumber >= 1;
  const isValidPageSize = Number.isFinite(pageSize) && pageSize > 0;

  if (!isValidPageNumber || !isValidPageSize) {
    return false;
  }

  return true;
}
 